"use client";

import buttonStyles from "@/components/atoms/Button/button.module.css";
import Input from "@/components/molecules/NewForm/Input";
import { GiftFormat, giftHooks } from "@/lib/future-centra/selection-fetchers";
import { useValidation } from "@/lib/hooks/useValidation";
import { yupResolver } from "@hookform/resolvers/yup";
import clsx from "clsx";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { Fragment, MouseEvent, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { CentraGiftCard } from "../../actions";
import { useGiftCardItem } from "../../hooks/useGiftCardItem";
import { GiftCardType, useGiftCardStore } from "../../hooks/useGiftCardStore";
import styles from "./index.module.css";

export const GiftCardForm = ({
  giftCards,
  locale,
}: {
  giftCards: CentraGiftCard[];
  locale: string;
}) => {
  const validation = useValidation();
  const t = useTranslations("giftCard");

  const [giftCardError, setGiftCardError] = useState<string>("");

  const storedGiftCardDetails = useGiftCardStore(state => ({
    "gift-card": state.giftCard,
    type: state.type,
    amount: state.amount,
    to: state.to,
    from: state.from,
    message: state.message,
  }));

  const storedGiftCardFns = useGiftCardStore(state => ({
    clear: state.clear,
    set: state.setDetails,
    verify: state.verify,
  }));

  const router = useRouter();
  const onSuccess = (data: GiftFormat | undefined) => {
    console.log("added gift card to selection");
    if (data?.selection?.items && data.selection.items.length > 0) {
      router.push(`${locale}/gift-cards/checkout`);
    }
  };
  const {
    isPending: isLoadingGiftCertificate,
    isError: isErrorGiftCertificate,
    mutate: addGiftCard,
  } = giftHooks.useAddGiftCertificate({ onSuccess });

  const {
    isPending: isLoadingCustomGiftCertificate,
    isError: isErrorCustomGiftCertificate,
    mutate: addCustomGiftCard,
  } = giftHooks.useAddCustomCertificate({ onSuccess });

  const isSubmitting = useMemo(
    () => isLoadingCustomGiftCertificate || isLoadingGiftCertificate,
    [isLoadingCustomGiftCertificate, isLoadingGiftCertificate],
  );

  const { data: selection } = giftHooks.useGiftSelection({
    select: (data: GiftFormat) => ({
      currency: data?.selection?.currency,
      countries: data.countries,
      currentCountry: data.location?.country,
      giftCard: data?.selection?.items?.[0],
    }),
  });

  const defaultValues = useMemo(
    () => ({
      "gift-card": storedGiftCardDetails["gift-card"] || "",
      type: storedGiftCardDetails.type || ("fixedValue" as GiftCardType),
      amount: storedGiftCardDetails.amount || 0,
      from: storedGiftCardDetails.from || "",
      to: storedGiftCardDetails.to || "",
      message: storedGiftCardDetails.message || "",
    }),
    [storedGiftCardDetails],
  );

  const minAmount = 10;
  const maxAmount = 10_000;

  const schema = validation
    .object({
      "gift-card": validation.string(),
      type: validation.string().oneOf(["fixedValue", "customValue"]),
      amount: validation
        .number()
        .transform(value => (isNaN(value) ? undefined : value))
        .optional()
        .when("type", {
          is: "customValue",
          then: schema =>
            schema
              .required(t("texts.wholeNumberRequired"))
              .test("is-integer", t("texts.wholeNumberRequired"), value => {
                return value === undefined || Number.isInteger(value);
              })
              .min(minAmount, `${t("texts.amountRequired")} ${minAmount}`)
              .max(maxAmount, `${t("texts.amountRequired")} ${maxAmount}`),
        }),

      // from: validation.string().required(t("texts.requiredFieldError")),
      // to: validation.string().required(t("texts.requiredFieldError")),
      message: validation.string().optional(),
    })
    .required();

  const form = useForm({
    defaultValues,
    resolver: yupResolver(schema),
  });

  const { setLocalGiftCardItem } = useGiftCardItem();

  const giftCardValue = form.watch("gift-card");
  const giftCardType = form.watch("type");

  const { handleSubmit } = form;

  const handleOnClickFixed = () => {
    setGiftCardError("");
    if (giftCardType !== "fixedValue") form.setValue("type", "fixedValue");
  };

  const handleOnClickCustom = () => {
    setGiftCardError("");
    if (giftCardType !== "customValue") form.setValue("type", "customValue");
  };

  const onSubmit = handleSubmit(formData => {
    setGiftCardError("");
    if (!formData["gift-card"]) {
      setGiftCardError(t("texts.giftCardRequired"));
      return;
    }
    if (!formData.amount && formData.type === "customValue") {
      console.error("Need amount for custom voucher");
    }

    if (formData.amount && formData.type === "customValue") {
      addCustomGiftCard({
        certificate: formData["gift-card"],
        amount: formData.amount,
      });

      storedGiftCardFns.set({
        // to: formData.to,
        to: "",
        // from: formData.from,
        from: "",
        giftCard: formData["gift-card"],
        amount: formData.amount,
        message: formData.message,
        type: formData.type,
      });
    }

    if (formData.type === "fixedValue") {
      addGiftCard({ certificate: formData["gift-card"] });
      storedGiftCardFns.set({
        // to: formData.to,
        to: "",
        // from: formData.from,
        from: "",
        giftCard: formData["gift-card"],
        type: formData.type,
        message: formData.message,
      });
    }

    setLocalGiftCardItem({
      id: formData["gift-card"],
      amount: formData.amount,
    });
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={onSubmit} className={styles.form}>
        <section>
          <div className={styles.giftCards}>
            {giftCards.map(card =>
              card.type === "predeterminedAmount" ? (
                <label
                  key={card.giftCertificate}
                  className={clsx(
                    buttonStyles.button,
                    buttonStyles.giftCard__default,
                    card.giftCertificate === giftCardValue &&
                      buttonStyles.giftCard__selected,
                  )}
                >
                  <Input
                    name="gift-card"
                    unstyled
                    inputProps={{
                      type: "radio",
                      value: card.giftCertificate,
                      onClick: handleOnClickFixed,
                      className: styles.hide,
                    }}
                  />
                  {
                    card.amountByCurrency?.find(
                      amount => amount.currency === selection?.currency,
                    )?.amount
                  }
                </label>
              ) : (
                <Fragment key={card.giftCertificate}>
                  <label
                    className={clsx(
                      buttonStyles.button,
                      buttonStyles.giftCard__default,
                      card.giftCertificate === giftCardValue &&
                        buttonStyles.giftCard__selected,
                    )}
                  >
                    <Input
                      name="gift-card"
                      unstyled
                      inputProps={{
                        type: "radio",
                        value: card.giftCertificate,
                        onClick: handleOnClickCustom,
                        className: styles.hide,
                      }}
                    />
                    {card.name}
                  </label>
                </Fragment>
              ),
            )}
          </div>
          {giftCardError && <p>{giftCardError}</p>}
        </section>

        <Input
          name="amount"
          label={`${t("texts.amount")} (${selection?.currency})`}
          inputProps={{
            ...form.register("amount"),
            type: "number",
            disabled: giftCardType !== "customValue",
            onClick: (e: MouseEvent<HTMLInputElement>) => {
              e.stopPropagation();
            },
          }}
          hide={giftCardType !== "customValue"}
          getError={message => <p>{message}</p>}
        />
        {/*
        <Input
          name="to"
          label={`${t("texts.to")} *`}
          inputProps={{
            type: "text",
            placeholder: t("texts.toPlaceholder"),
          }}
          getError={message => <p>{message}</p>}
        />

        <Input
          name="from"
          label={`${t("texts.from")} *`}
          inputProps={{
            type: "text",
            placeholder: t("texts.fromPlaceholder"),
          }}
          getError={message => <p>{message}</p>}
        />

         <Input
          name="message"
          label={t("texts.message")}
          inputProps={{
            type: "text",
            placeholder: t("texts.messagePlaceholder"),
          }}
        /> */}
        <input
          type="submit"
          value={t("texts.checkoutCta")}
          disabled={isSubmitting}
          className={clsx(buttonStyles.button, buttonStyles.full)}
        />
      </form>
    </FormProvider>
  );
};

export const Note = ({ text }: { text?: string }) => {
  if (!text) return null;

  return (
    <div className={styles.note}>
      <span>{text}</span>
    </div>
  );
};
